/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { SubSink } from 'subsink';
import { FormErrorParams } from '@shared/model';

interface FormErrorMessage {
  [key: string]: { label: string; params: (error: FormErrorParams) => any[] };
}

const errorMessages: FormErrorMessage = {
  required: { label: 'Required', params: () => [null, null] },
  phoneFax: { label: 'Must be a valid number', params: () => [null, null] },
  email: { label: 'Must be a valid email address', params: () => [null, null] },
  max: { label: 'Maximum value is $1', params: (error: FormErrorParams) => [error.max, null] },
  min: { label: 'Minimum value is $1', params: (error: FormErrorParams) => [error.min, null] },
  maxlength: { label: 'Must be up to $1 characters long', params: (error: FormErrorParams) => [error.requiredLength, null] },
  minlength: { label: 'Must be at least $1 characters long', params: (error: FormErrorParams) => [error.requiredLength, null] },
  noValues: { label: 'Not available', params: () => [null, null] },
  dateIsAfterToday: { label: 'Must be before the current date\time', params: () => [null, null] },
  dayIsBeforeToday: { label: 'Must be after the current date\time', params: () => [null, null] },
  lettersNumbers: { label: 'Only letters and numbers', params: () => [null, null] },
  letters: { label: 'Only letters', params: () => [null, null] },
  charactersRange: { label: 'Must be a value between $1 and $2', params: (error: FormErrorParams) => [error.minValue, error.maxValue] },
  amountOfCharacters: { label: 'Must be $1 characters', params: (error: FormErrorParams) => [error.exactLength, null] },
  wholeNumbersRange: { label: 'Only whole numbers between $1 and $2', params: (error: FormErrorParams) => [error.minValue, error.maxValue] },
  numbersRange: { label: 'Must be a value between $1 and $2 numbers long', params: (error: FormErrorParams) => [error.minValue, error.maxValue] },
  lettersRange: { label: 'Must be a value between $1 and $2 letters long', params: (error: FormErrorParams) => [error.minValue, error.maxValue] },
  amountOfLettersNumbers: { label: 'Must be $1 characters, letters and numbers', params: (error: FormErrorParams) => [error.exactLength, null] },
  customFormat: { label: 'Must be a value in the format $1', params: (error: FormErrorParams) => [error.example, null] },
  notIntegerNumber: { label: 'Only an integer number', params: () => [null, null] },
  notDecimalNumber: { label: 'Only a decimal number', params: () => [null, null] },
  whitespace: { label: 'Invalid entry', params: () => [null, null] }
};
@Component({
    selector: 'errors-messages',
    templateUrl: './errors-messages.component.html',
    styleUrls: ['errors-messages.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ErrorsMessagesComponent implements OnChanges, OnDestroy {
  @Input() control!: FormControl | AbstractControl | null;
  errorText!: string | undefined;

  private readonly INVALID_STATUS = 'INVALID';
  private readonly subs = new SubSink();

  constructor(private readonly ref: ChangeDetectorRef) {}

  ngOnChanges(ev: SimpleChanges) {
    if (ev.control.currentValue) {
      this.setErrors();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  setErrors() {
    if (this.control) {
      if (this.control.status === this.INVALID_STATUS) {
        this.showError(this.control);
      }
      this.subs.unsubscribe();
      this.subs.sink = this.control.statusChanges.subscribe(status => {
        if (status === this.INVALID_STATUS && this.control?.errors) {
          this.showError(this.control);
        } else {
          this.removeError();
        }
      });
    }
  }

  private showError(control: FormControl | AbstractControl) {
    this.errorText = this.setValidationMsg(control.errors);
    this.ref.markForCheck();
  }

  private removeError(): void {
    this.errorText = '';
    this.ref.markForCheck();
  }

  setValidationMsg(valErrors: ValidationErrors | null): string | undefined {
    let errorMsg: string | undefined = undefined;
    if (valErrors) {
      const errorKey = Object.keys(valErrors)[0] as keyof FormErrorMessage;
      const errorValue = valErrors[errorKey];
      const errorMessageItem = errorMessages[errorKey];

      if (errorMessageItem) {
        const [errorParam1, errorParam2] = errorMessageItem.params(errorValue);

        errorMsg = errorMessageItem.label;
        if (errorParam1 !== null) {
          errorMsg = errorMsg.replace('$1', errorParam1);
        }
        if (errorParam2 !== null) {
          errorMsg = errorMsg.replace('$2', errorParam2);
        }
      }
    }
    return errorMsg;
  }
}
